import { useState, useEffect, useRef } from 'react';

import PropTypes from 'prop-types';

import Modal from '#globalComponents/Overlay/Modal';

import OAuthFlowStep from './OAuthFlowStep';
import OAuthInfoStep from './OAuthInfoStep';
import ProviderSelectionStep from './ProviderSelectionStep';
import SmtpFormStep from './SmtpFormStep';

const EmailAccountModal = ({ isOpen, onClose, account, onSave }) => {
	const isEditMode = Boolean(account);

	// Step state: 'provider-selection' | 'oauth-flow' | 'oauth-info' | 'smtp-form'
	const [currentStep, setCurrentStep] = useState('provider-selection');
	const [selectedProvider, setSelectedProvider] = useState(null);
	// Only the SMTP step is a form, and the surface must be the <form> for the Panel slots to stay
	// direct flex children — so that step hands its submit handler up instead of nesting a <form>.
	const smtpSubmitRef = useRef(null);

	// Reset step when modal opens/closes or account changes
	useEffect(() => {
		if (isEditMode) {
			// For edit mode, check if it's an OAuth account
			if (account.authStrategy === 'oauth2') {
				// OAuth account - show OAuth info step
				setCurrentStep('oauth-info');
				setSelectedProvider(account.provider);
			} else {
				// SMTP account - show SMTP form
				setCurrentStep('smtp-form');
				setSelectedProvider(account.provider || 'custom');
			}
		} else {
			// For add mode, start with provider selection
			setCurrentStep('provider-selection');
			setSelectedProvider(null);
		}
	}, [isOpen, account, isEditMode]);

	const handleSelectProvider = (provider) => {
		setSelectedProvider(provider);
		if (provider === 'outlook') {
			setCurrentStep('oauth-flow');
		} else {
			setCurrentStep('smtp-form');
		}
	};

	const handleOAuthSuccess = () => {
		// OAuth successful - close modal and let parent refresh data
		handleClose();
	};

	const handleReauthorizeSuccess = () => {
		// Reauthorization successful - close modal and let parent refresh data
		handleClose();
	};

	const handleSmtpSave = (payload) => {
		onSave(payload);
	};

	const handleBackToProviderSelection = () => {
		setCurrentStep('provider-selection');
		setSelectedProvider(null);
	};

	const handleClose = () => {
		setCurrentStep('provider-selection');
		setSelectedProvider(null);
		onClose();
	};

	const isSmtpForm = currentStep === 'smtp-form';

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			size="l"
			as={isSmtpForm ? 'form' : undefined}
			onSubmit={isSmtpForm ? (e) => smtpSubmitRef.current?.(e) : undefined}
		>
			{currentStep === 'provider-selection' && (
				<ProviderSelectionStep onSelectProvider={handleSelectProvider} onClose={handleClose} />
			)}

			{currentStep === 'oauth-flow' && (
				<OAuthFlowStep
					provider={selectedProvider}
					onSuccess={handleOAuthSuccess}
					onBack={handleBackToProviderSelection}
					onClose={handleClose}
				/>
			)}

			{currentStep === 'oauth-info' && (
				<OAuthInfoStep
					account={account}
					onReauthorizeSuccess={handleReauthorizeSuccess}
					onClose={handleClose}
				/>
			)}

			{isSmtpForm && (
				<SmtpFormStep
					account={account}
					onSave={handleSmtpSave}
					onBack={isEditMode ? null : handleBackToProviderSelection}
					onClose={handleClose}
					submitRef={smtpSubmitRef}
				/>
			)}
		</Modal>
	);
};

EmailAccountModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	account: PropTypes.object, // null for new account, object for editing
	onSave: PropTypes.func.isRequired,
};

export default EmailAccountModal;
